import React, { useState, useEffect, useRef } from 'react';
import { apiErrorText } from '../lib/apiError';
import { useSWRConfig } from 'swr';
import { orgApi } from '../utils/apiV2';
import api from '../utils/api'; // For legacy endpoints not yet migrated
import { createManualBooking } from '../utils/manualBooking';
import confetti from 'canvas-confetti';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Zap,
  Plus,
  Clock,
  Users,
  Package,
  Check,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  List,
  Upload,
  Filter,
  AlertCircle,
  MoreVertical,
  ChevronDown,
  CheckSquare,
  Sparkles,
  Loader2,
  Rocket,
  User,
  Activity,
  Settings,
  Phone,
  Mail,
  Edit,
  Eye,
  Trash2,
  Link2,
  MapPin,
  ExternalLink,
  CalendarDays,
  CreditCard,
  FileSignature
} from 'lucide-react';
import TabPageLayout, { PageLoader } from './TabPageLayout';
import BookingCard from './BookingCard';
// PageGuide and GUIDE_CONTENT removed - using TabPageLayout standard
import { useSettings } from '../contexts/SettingsContext';
import { formatTime, formatDate } from '../utils/formatters';
import { toast } from 'sonner';
import AutoSchedulerWarningsDialog from './AutoSchedulerWarningsDialog';
// BulkImportDialog removed - feature deprecated
import { Input } from './ui/input';
import { MultiChipTabs } from './ui/chip-tabs';
import SourceFilter from './SourceFilter';
import { useSourceFilter } from '../utils/bookingSource';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarPicker } from './ui/calendar';
import { format } from 'date-fns';
import { openLink } from '../utils/openLink';
import CardListViewToggle from './CardListViewToggle';
import StandardModal from './StandardModal';
import TimeSlotSelector from './TimeSlotSelector';
import { PrimaryButton, SecondaryButton, LayoutActionButton } from './Buttons';
import TeamMemberSelector from './TeamMemberSelector';
import AssetSelector from './AssetSelector';
import RoleSelector from './RoleSelector';
import { useRoles } from '../hooks/useData';
import { Textarea } from './ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import MassActionToolbar from './MassActionToolbar';
import EditBookingModal from './EditBookingModal';
import AddBookingWizard from './AddBookingWizard';
import SharePaymentLinkModal from './SharePaymentLinkModal';
import SmartSchedulePanel from './SmartSchedulePanel';
import KanbanView from './KanbanView';
import { getTimezoneAbbr } from '../utils/timezoneUtils';
import { DEFAULT_TIMEZONE } from '../utils/timezones';

// Fetch EVERY booking page-by-page. The backend caps a single request at 500
// rows (default 100, newest-first) — busy orgs were silently losing their
// older trips off the calendar because only the first page ever loaded.
const fetchAllBookings = async (params) => {
  const pageSize = 500;
  const hardCap = 10000; // safety valve
  let offset = 0;
  let all = [];
  for (;;) {
    const res = await orgApi.getBookings({ ...params, limit: pageSize, offset });
    const page = res?.bookings || [];
    all = all.concat(page);
    const total = typeof res?.total === 'number' ? res.total : all.length;
    offset += pageSize;
    if (page.length < pageSize || all.length >= total || offset >= hardCap) break;
  }
  return { bookings: all, total: all.length };
};

const CalendarScheduler = ({ onRefresh, readOnly = false, teamMemberId = null, showMyBookingsToggle = false, onDemoClick, demoLoading = false, editBookingId = null, onEditBookingClear = null, autoOpenAddBooking = false, onAutoOpenAddBookingClear = null }) => {
  const { timeFormat, dateFormat } = useSettings();
  const { roles } = useRoles(); // Add roles
  const { mutate: globalMutate } = useSWRConfig(); // For invalidating ScheduleTab cache
  
  // Helper function to format customer name to "First L."
  const formatCustomerName = (fullName) => {
    if (!fullName) return '';
    const names = fullName.split(' ');
    const firstName = names[0];
    const lastInitial = names[names.length - 1]?.[0] || '';
    return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
  };
  
  // Format a Date object as YYYY-MM-DD using LOCAL timezone (not UTC)
  // CRITICAL: toISOString().split('T')[0] gives WRONG date in non-UTC timezones!
  const toLocalDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  
  // Helper function to format date like "Jan 1st, 2026"
  const formatDatePretty = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr + 'T00:00:00'); // Force local timezone
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = months[date.getMonth()];
      const day = date.getDate();
      const year = date.getFullYear();
      
      // Add ordinal suffix (1st, 2nd, 3rd, etc.)
      const getOrdinal = (n) => {
        const s = ['th', 'st', 'nd', 'rd'];
        const v = n % 100;
        return s[(v - 20) % 10] || s[v] || s[0];
      };
      
      return `${month} ${day}${getOrdinal(day)}, ${year}`;
    } catch (e) {
      return dateStr;
    }
  };
  
  const [viewMode, setViewMode] = useState('calendar'); // calendar, card
  const [cardViewTab, setCardViewTab] = useState('upcoming'); // upcoming, completed
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [trips, setBookings] = useState([]);
  // Front-end channel show/hide (BookingFlows / GetMyBoat / GetYourGuide).
  // Raw fetch results live in a ref so toggling re-filters without refetching.
  const [sourceKeys, toggleSource, sourceFilterFn] = useSourceFilter();
  const allTripsRef = useRef([]);

  // Re-apply the channel filter instantly when a source chip is toggled — no refetch.
  useEffect(() => {
    if (allTripsRef.current.length > 0) {
      setBookings(allTripsRef.current.filter(sourceFilterFn));
    }
  }, [sourceFilterFn]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [services, setServices] = useState([]); // For add-ons in Edit Modal
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDayBookings, setSelectedDayBookings] = useState([]);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [warningsDialogOpen, setWarningsDialogOpen] = useState(false);
  const [schedulerResult, setSchedulerResult] = useState(null);
  const [selectedTeamMemberFilters, setSelectedTeamMemberFilters] = useState([]); // Filter trips by teamMember in day modal
  
  // Date range for list/card views
  const [dateRangePreset, setDateRangePreset] = useState('next7'); // today, next7, next30, custom
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // "See My Bookings" toggle for teamMember view
  const [showMyBookingsOnly, setShowMyBookingsOnly] = useState(showMyBookingsToggle);
  
  // Single trip edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editTeamMemberId, setEditTeamMemberId] = useState('');
  const [editAssetId, setEditAssetId] = useState('');
  
  // Add Booking modal
  const [addBookingModalOpen, setAddBookingModalOpen] = useState(false);
  const [newBooking, setNewBooking] = useState({
    date: '',
    startTime: '',
    endTime: '',
    duration: '',
    groupSize: '',
    teamMemberId: '',
    assetId: '',
    notes: '',
    teamAssignments: [], // [{teamId, teamName, roleId, roleName}]
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    paymentStatus: 'unpaid', // unpaid, paid, ready_for_card
    totalAmount: 0,
    paymentMethod: '' // cash, card, stripe, manual
  });
  
  // Bulk Import modal
  // Bulk import removed - feature deprecated
  
  // Status filter
  const [statusFilter, setStatusFilter] = useState('all'); // all, unassigned, partial, assigned
  const [paymentFilter, setPaymentFilter] = useState(['paid', 'deposit', 'partial', 'unpaid']); // multi-select, mirrors Manifest defaults

  // Swipe-down-to-close for the mobile day-detail bottom sheet
  const sheetTouchStartY = useRef(null);
  const handleSheetTouchStart = (e) => { sheetTouchStartY.current = e.touches[0].clientY; };
  const handleSheetTouchEnd = (e) => {
    if (sheetTouchStartY.current == null) return;
    const dy = e.changedTouches[0].clientY - sheetTouchStartY.current;
    if (dy > 60) setDayModalOpen(false);
    sheetTouchStartY.current = null;
  };

  // Health Check modal
  const [healthModalOpen, setHealthModalOpen] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [autoScheduling, setAutoScheduling] = useState(false);

  // Mass actions state
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tripToDelete, setBookingToDelete] = useState(null); // For single trip deletion
  const [massActionMode, setMassActionMode] = useState(false);
  const [showResourceOverview, setShowResourceOverview] = useState(false); // Resource overview collapse
  const [expandedBookingDetails, setExpandedBookingDetails] = useState({}); // Track which trip's customer details are expanded
  const [copiedBookingId, setCopiedBookingId] = useState(null); // Track which trip link was just copied
  const [showWaiverDetailsModal, setShowWaiverDetailsModal] = useState(false);
  const [selectedWaiverBooking, setSelectedWaiverBooking] = useState(null);
  const [updateTimeouts, setUpdateTimeouts] = useState({}); // Debounce trip updates
  const [emergencyPhone, setEmergencyPhone] = useState(
    localStorage.getItem('orgEmergencyPhone') || '(512) 555-5555'
  );

  // Payment link modal state (for "Pay Later" bookings)
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [paymentLinkData, setPaymentLinkData] = useState(null);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);

  // Contact modal state (for phone/email with modal instead of direct call)
  const [contactModal, setContactModal] = useState({ open: false, type: null, value: null, name: null, booking: null });
  const [resendingConfirmation, setResendingConfirmation] = useState(false);
  const [collectingBookingId, setCollectingBookingId] = useState(null); // Track which booking is being collected

  // Shimmer effect state
  const [shimmerActive, setShimmerActive] = useState(false);

  // Auto-scheduler confirmation modal state
  const [showSchedulerConfirm, setShowSchedulerConfirm] = useState(false);
  const [schedulerPreview, setSchedulerPreview] = useState(null);
  const [isScheduling, setIsScheduling] = useState(false);
  
  // Smart Schedule Panel state
  const [smartScheduleOpen, setSmartScheduleOpen] = useState(false);
  
  // View Ticket Modal state (mobile-friendly)
  const [viewTicketModalOpen, setViewTicketModalOpen] = useState(false);
  const [viewTicketUrl, setViewTicketUrl] = useState(null);
  const [loadingTicketUrl, setLoadingTicketUrl] = useState(false);
  
  // Actions popover state
  const [actionsPopoverOpen, setActionsPopoverOpen] = useState(false);

  // Organization timezone (source of truth from settings)
  // Default to America/Chicago (CST) for US orgs if not set
  const orgTimezone = localStorage.getItem('orgTimezone') || 'America/Chicago';
  const getTimezoneAbbreviation = (tz) => {
    return getTimezoneAbbr(tz);
  };

  // Initialize default date range (Next 7 Days) on mount
  useEffect(() => {
    applyDateRangePreset('next7');
  }, []);

  // Shimmer effect every 18 seconds
  useEffect(() => {
    const shimmerInterval = setInterval(() => {
      setShimmerActive(true);
      setTimeout(() => setShimmerActive(false), 2000); // Duration of shimmer animation
    }, 18000); // Every 18 seconds

    return () => clearInterval(shimmerInterval);
  }, []);

  useEffect(() => {
    fetchData('all'); // initial load
    
    // Expose refresh function for child components
    window.refreshBookingData = async () => {
      await fetchData(paymentFilter);
      if (onRefresh) onRefresh();
    };
    
    return () => {
      delete window.refreshBookingData;
    };
  }, []); // mount only

  // Update selected day trips when trips state changes and modal is open
  useEffect(() => {
    if (dayModalOpen && selectedDate) {
      const freshBookings = trips.filter(t => t.date === selectedDate).sort((a, b) => a.startTime.localeCompare(b.startTime));
      setSelectedDayBookings(freshBookings);
    }
  }, [trips, selectedDate, dayModalOpen]);

  // Handle edit booking from Schedule tab navigation
  useEffect(() => {
    if (editBookingId && trips.length > 0) {
      const bookingToEdit = trips.find(t => t.id === editBookingId);
      // Validate it's a proper booking object (not an error object)
      if (bookingToEdit && bookingToEdit.id && !bookingToEdit.error) {
        setEditingBooking(bookingToEdit);
        setEditModalOpen(true);
        // Clear the editBookingId after opening modal
        if (onEditBookingClear) {
          onEditBookingClear();
        }
      } else if (onEditBookingClear) {
        // Clear invalid editBookingId
        onEditBookingClear();
      }
    }
  }, [editBookingId, trips, onEditBookingClear]);

  // "+ New booking" deep-link from the Manifest → auto-open the Add Booking wizard
  useEffect(() => {
    if (autoOpenAddBooking) {
      setAddBookingModalOpen(true);
      if (onAutoOpenAddBookingClear) onAutoOpenAddBookingClear();
    }
  }, [autoOpenAddBooking, onAutoOpenAddBookingClear]);

  const fetchData = async (filter) => {
    // filter is passed explicitly to avoid stale closure issues with paymentFilter state
    const raw = filter !== undefined ? filter : paymentFilter;
    const activeFilter = Array.isArray(raw) ? raw.join(',') : raw;
    try {
      setLoading(true);
      
      // Calculate date range for efficient loading
      // Extended range: 365 days back (operators review past seasons' trips —
      // financial history must stay visible) to 365 days ahead for far-future bookings
      const today = new Date();
      const startDateRange = new Date(today);
      startDateRange.setDate(startDateRange.getDate() - 365);
      const endDateRange = new Date(today);
      endDateRange.setDate(endDateRange.getDate() + 365);
      
      const startParam = toLocalDateStr(startDateRange);
      const endParam = toLocalDateStr(endDateRange);
      
      // Use PostgreSQL v2 APIs - use allSettled so one failing call doesn't block the rest
      // (team members with restricted permissions may not have access to all endpoints)
      const [tripsResult, teamMembersResult, assetsResult, servicesResult] = await Promise.allSettled([
        fetchAllBookings({ startDate: startParam, endDate: endParam, paymentFilter: activeFilter }),
        orgApi.getTeamMembers(),
        orgApi.getAssets(),
        orgApi.getServices()
      ]);

      // Extract values, defaulting to empty on failure
      const tripsRes = tripsResult.status === 'fulfilled' ? tripsResult.value : null;
      const teamMembersRes = teamMembersResult.status === 'fulfilled' ? teamMembersResult.value : null;
      const assetsRes = assetsResult.status === 'fulfilled' ? assetsResult.value : null;
      const servicesRes = servicesResult.status === 'fulfilled' ? servicesResult.value : null;

      // If the primary bookings call failed, show error and bail out
      if (tripsResult.status === 'rejected') {
        const err = tripsResult.reason;
        const status = err?.response?.status;
        if (status === 403) {
          toast.error("You don't have permission to view bookings");
        } else {
          console.error('Error fetching bookings:', err);
          toast.error('Failed to load schedule data');
        }
        setLoading(false);
        return;
      }
      
      // Transform v2 response format to component expected format
      let bookingsData = (tripsRes?.bookings || tripsRes || []).map(booking => ({
        ...booking,
        // Map v2 field names to legacy field names for backward compatibility
        preferredDate: booking.bookingDate || booking.preferredDate,
        preferredTime: booking.startTime || booking.preferredTime,
        date: booking.bookingDate || booking.preferredDate || booking.date,
        startTime: booking.startTime || booking.preferredTime,
        // Duration
        duration: booking.durationMinutes || booking.duration,
        durationMinutes: booking.durationMinutes,
        // Keep service/asset/team references
        serviceId: booking.serviceId,
        assetId: booking.assetId,
        teamMemberId: booking.teamMemberId,
        // Customer info
        customerName: booking.customerName,
        customerEmail: booking.customerEmail,
        customerPhone: booking.customerPhone,
        partySize: booking.partySize || 1,
        emergencyContactNumber: booking.emergencyContactNumber,
        // Location
        city: booking.city,
        country: booking.country,
        pickupLocation: booking.pickupLocation,
        // Notes
        notes: booking.notes || booking.internalNotes,
        internalNotes: booking.internalNotes,
        customerNotes: booking.customerNotes,
        // Pricing
        servicePrice: booking.totalPrice || booking.servicePrice,
        totalPrice: booking.totalPrice,
        totalAmount: booking.totalAmount,
        amountPaid: booking.amountPaid,
        paidAmount: booking.paidAmount || booking.amountPaid,
        depositPaid: booking.depositPaid,
        remainingBalance: booking.remainingBalance,
        // Add-ons
        selectedAddOns: booking.selectedAddOns || booking.add_ons || [],
        // Status
        status: booking.status,
        paymentStatus: booking.paymentStatus,
        checkedIn: booking.checkedIn,
        waiverSigned: booking.waiverSigned,
        // Timeline timestamps
        createdAt: booking.createdAt,
        paidAt: booking.paidAt,
        waiverSignedAt: booking.waiverSignedAt,
        // Team assignments
        teamAssignments: booking.teamAssignments || [],
        // Timezone
        timezone: booking.timezone,
        // Meta
        confirmationCode: booking.confirmationCode,
        id: booking.id
      }));
      
      // Never surface cancelled bookings on the calendar unless the Cancelled chip is on
      const filterList = Array.isArray(raw) ? raw : String(raw || '').split(',');
      if (!filterList.includes('cancelled')) {
        bookingsData = bookingsData.filter(b => !['cancelled', 'canceled'].includes((b.status || '').toLowerCase()));
      }
      allTripsRef.current = bookingsData;
      setBookings(bookingsData.filter(sourceFilterFn));
      setTeamMembers(teamMembersRes?.data?.teamMembers || teamMembersRes?.data || []);
      setAssets(assetsRes?.data?.assets || assetsRes?.data || []);
      setServices(servicesRes?.data?.services || servicesRes?.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get month start date
  const getMonthStart = () => {
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return toLocalDateStr(start);
  };

  // Helper to get month end date
  const getMonthEnd = () => {
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    return toLocalDateStr(end);
  };

  // Helper function to detect orphaned references
  const detectOrphanedReferences = (trip) => {
    const issues = [];
    
    // Check if teamMember exists
    if (trip.teamMemberId && trip.teamMemberId !== 'unassigned') {
      const teamMemberExists = teamMembers.some(c => c.id === trip.teamMemberId);
      if (!teamMemberExists) {
        issues.push('teamMember');
      }
    }
    
    // Check if asset exists
    if (trip.assetId && trip.assetId !== 'unassigned') {
      const assetExists = assets.some(b => b.id === trip.assetId);
      if (!assetExists) {
        issues.push('asset');
      }
    }
    
    return issues;
  };

  // Get trip status - accounts for orphaned references and team assignments
  const getBookingStatus = (trip) => {
    const orphanedIssues = detectOrphanedReferences(trip);
    
    // If teamMember or asset is orphaned (deleted), treat as missing
    const hasTeamMember = trip.teamMemberId && trip.teamMemberId !== 'unassigned' && !orphanedIssues.includes('teamMember');
    const hasAsset = trip.assetId && trip.assetId !== 'unassigned' && !orphanedIssues.includes('asset');
    
    // Check team assignments
    const assignedTeam = (trip.teamAssignments || []).filter(a => a.teamId && a.teamId !== '');
    const hasTeam = assignedTeam.length > 0;
    
    // Check if team is required for this booking
    // Look at the asset's teamRequirements - if empty or missing, team is not required
    const asset = assets?.find(a => a.id === trip.assetId);
    
    // Team is required if asset has teamRequirements with roles
    // OR if there are team assignment slots defined (which means service requires team)
    const assetRequiresTeam = asset?.teamRequirements?.length > 0;
    const hasPendingTeamSlots = trip.teamAssignments?.length > 0;
    const teamRequired = assetRequiresTeam || hasPendingTeamSlots;
    
    // If team is NOT required, consider assigned when just asset is set
    if (!teamRequired) {
      if (hasAsset) {
        return 'assigned';
      } else {
        return 'unassigned';
      }
    }
    
    // Team IS required - check both asset and team
    if (hasAsset && hasTeam) {
      // Check if all team slots are filled
      const allFilled = (trip.teamAssignments || []).every(a => a.teamId && a.teamId !== '');
      return allFilled ? 'assigned' : 'partial';
    } else if (hasAsset || hasTeam) {
      return 'partial';
    } else {
      return 'unassigned';
    }
  };

  // Get trip status color - simple colors based on accurate status
  const getBookingStatusColor = (trip) => {
    const status = getBookingStatus(trip);
    
    // Check for unpaid status first - amber indicator takes priority
    const isUnpaid = trip.paymentStatus && 
      !['paid', 'refunded', 'partial_refund'].includes(trip.paymentStatus);
    
    if (isUnpaid) {
      return {
        bg: 'bg-amber-100',
        border: 'border-amber-400',
        text: 'text-amber-800',
        badge: 'bg-amber-500',
        label: '💳 Unpaid',
        color: '#9333ea'
      };
    }
    
    if (status === 'assigned') {
      return {
        bg: 'bg-green-100',
        border: 'border-green-400',
        text: 'text-green-800',
        badge: 'bg-green-500',
        label: 'Assigned',
        color: '#22c55e'
      };
    } else if (status === 'partial') {
      return {
        bg: 'bg-amber-100',
        border: 'border-amber-400',
        text: 'text-amber-800',
        badge: 'bg-amber-500',
        label: 'Partial',
        color: '#f59e0b'
      };
    } else {
      return {
        bg: 'bg-red-100',
        border: 'border-red-300',
        text: 'text-gray-700',
        badge: 'bg-red-400',
        label: 'Unassigned',
        color: '#ef4444'
      };
    }
  };

  // Get currently visible/filtered bookings for smart scheduling
  const getVisibleBookings = () => {
    // Apply status filter
    let filtered = trips;
    if (statusFilter !== 'all') {
      filtered = filtered.filter(trip => {
        const status = getBookingStatus(trip);
        if (statusFilter === 'unpaid') {
          return trip.paymentStatus && !['paid', 'refunded', 'partial_refund'].includes(trip.paymentStatus);
        }
        return status === statusFilter;
      });
    }
    
    // Apply date range filter for list view
    if (viewMode === 'list' && startDate && endDate) {
      filtered = filtered.filter(trip => 
        trip.date >= startDate && trip.date <= endDate
      );
    }
    
    // Apply "my bookings" filter if enabled
    if (showMyBookingsOnly && teamMemberId) {
      filtered = filtered.filter(trip => 
        trip.teamMemberId === teamMemberId || 
        trip.teamAssignments?.some(a => a.teamId === teamMemberId)
      );
    }
    
    return filtered;
  };

  // Count unscheduled bookings with breakdown (for Smart Schedule button)
  // Counts only visible/filtered bookings for consistency with panel
  // Uses same logic as getBookingStatus to properly detect what's needed
  const getUnscheduledBreakdown = () => {
    let needsAsset = 0;
    let needsTeam = 0;
    let needsBoth = 0;
    
    const visibleBookings = getVisibleBookings();
    visibleBookings.forEach(trip => {
      const hasAsset = trip.assetId && trip.assetId !== 'unassigned';
      
      // Check team assignments - similar logic to getBookingStatus
      const assignedTeam = (trip.teamAssignments || []).filter(a => a.teamId && a.teamId !== '');
      const hasTeam = assignedTeam.length > 0;
      
      // Check if team is required for this booking
      const asset = assets?.find(a => a.id === trip.assetId);
      const assetRequiresTeam = asset?.teamRequirements?.length > 0;
      const hasPendingTeamSlots = trip.teamAssignments?.length > 0;
      const teamRequired = assetRequiresTeam || hasPendingTeamSlots;
      
      // Determine what's needed
      const needsAssetAssign = !hasAsset;
      const needsTeamAssign = teamRequired && !hasTeam;
      
      // Also check if team slots are only partially filled
      const allTeamFilled = !teamRequired || (trip.teamAssignments || []).every(a => a.teamId && a.teamId !== '');
      const needsMoreTeam = teamRequired && !allTeamFilled;
      
      if (needsAssetAssign && (needsTeamAssign || needsMoreTeam)) {
        needsBoth++;
      } else if (needsAssetAssign) {
        needsAsset++;
      } else if (needsTeamAssign || needsMoreTeam) {
        needsTeam++;
      }
    });
    
    return { needsAsset, needsTeam, needsBoth, total: needsAsset + needsTeam + needsBoth };
  };
  
  // Simple count for badge
  const getUnscheduledCount = () => {
    return getUnscheduledBreakdown().total;
  };

  // Date range preset functions
  const applyDateRangePreset = (preset) => {
    const today = new Date();
    // Use local timezone for date string
    const formatLocalDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    let start, end;
    
    switch (preset) {
      case 'today':
        start = end = formatLocalDate(today);
        break;
      case 'next7':
        start = formatLocalDate(today);
        end = formatLocalDate(new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000));
        break;
      case 'next30':
        start = formatLocalDate(today);
        end = formatLocalDate(new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000));
        break;
      case 'custom':
        setShowCustomDatePicker(true);
        return;
      default:
        return;
    }
    
    setDateRangePreset(preset);
    setStartDate(start);
    setEndDate(end);
    setShowCustomDatePicker(false);
  };

  const handleCustomDateApply = () => {
    if (startDate && endDate) {
      setDateRangePreset('custom');
      setShowCustomDatePicker(false);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const getFilteredBookings = () => {
    let filteredBookings = trips;
    
    // Filter by teamMember if "See My Bookings" is enabled
    if (showMyBookingsToggle && showMyBookingsOnly && teamMemberId) {
      filteredBookings = filteredBookings.filter(trip => {
        // Check direct assignment (primary field) OR teamAssignments array
        const isDirectAssigned = trip.teamMemberId === teamMemberId;
        const isInAssignments = trip.teamAssignments?.some(assignment => assignment.teamId === teamMemberId);
        return isDirectAssigned || isInAssignments;
      });
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filteredBookings = filteredBookings.filter(trip => {
        if (statusFilter === 'unpaid') {
          // Filter for unpaid bookings
          const total = trip.totalAmount || trip.totalPrice || trip.servicePrice || 0;
          const paid = trip.amountPaid || trip.amount_paid || 0;
          const paymentStatus = trip.paymentStatus;
          return paymentStatus !== 'paid' && paid < total;
        }
        // Filter by assignment status
        const status = getBookingStatus(trip);
        return status === statusFilter;
      });
    }
    
    // Don't apply date range filtering for calendar view
    if (viewMode === 'calendar') {
      return filteredBookings;
    }
    
    // Filter by date range for list/card views
    if (!startDate || !endDate) {
      return filteredBookings;
    }
    
    return filteredBookings.filter(trip => {
      return trip.date >= startDate && trip.date <= endDate;
    });
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getBookingsForDate = (dateStr) => {
    let dateBookings = trips.filter(t => t.date === dateStr);
    
    if (dateBookings.length === 0 && trips.length > 0) {
    }
    
    // Apply "See My Bookings Only" filter if enabled
    if (showMyBookingsToggle && showMyBookingsOnly && teamMemberId) {
      dateBookings = dateBookings.filter(trip => {
        // Check direct assignment (primary field) OR teamAssignments array
        const isDirectAssigned = trip.teamMemberId === teamMemberId;
        const isInAssignments = trip.teamAssignments?.some(assignment => assignment.teamId === teamMemberId);
        return isDirectAssigned || isInAssignments;
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      dateBookings = dateBookings.filter(trip => {
        if (statusFilter === 'unpaid') {
          // Filter for unpaid bookings
          const total = trip.totalAmount || trip.totalPrice || trip.servicePrice || 0;
          const paid = trip.amountPaid || trip.amount_paid || 0;
          const paymentStatus = trip.paymentStatus;
          return paymentStatus !== 'paid' && paid < total;
        }
        // Filter by assignment status
        const status = getBookingStatus(trip);
        return status === statusFilter;
      });
    }
    
    return dateBookings;
  };

  const getDateStatus = (dateStr) => {
    const dayBookings = getBookingsForDate(dateStr);
    if (dayBookings.length === 0) return 'none';
    
    // Check for unpaid bookings first (highest priority for visual indicator)
    const hasUnpaid = dayBookings.some(t => {
      const total = t.totalAmount || t.totalPrice || t.servicePrice || 0;
      const paid = t.amountPaid || t.amount_paid || 0;
      return t.paymentStatus !== 'paid' && paid < total && total > 0;
    });
    
    if (hasUnpaid) return 'unpaid';
    
    // Use getBookingStatus to check actual status (accounts for orphaned references)
    const allAssigned = dayBookings.every(t => getBookingStatus(t) === 'assigned');
    const allUnassigned = dayBookings.every(t => getBookingStatus(t) === 'unassigned');
    
    if (allAssigned) return 'assigned';
    if (allUnassigned) return 'unassigned';
    return 'partial';
  };

  const handleEditClick = async (trip) => {
    // Mark booking as viewed if it's new
    if (trip.isNew !== false) {
      try {
        await api.post(`/bookings/${trip.id}/mark-viewed`);
        // Update local state
        setBookings(prevBookings => 
          prevBookings.map(b => b.id === trip.id ? {...b, isNew: false} : b)
        );
      } catch (error) {
      }
    }
    
    // Open single trip edit modal
    setEditingBooking(trip);
    setEditTeamMemberId(trip.teamMemberId || 'unassigned');
    setEditAssetId(trip.assetId || 'unassigned');
    setEditModalOpen(true);
  };

  // Handle viewing ticket in modal (mobile-friendly)
  const handleViewTicket = async (trip) => {
    // Open a tab synchronously (within the user gesture) so iOS Safari doesn't block
    // the popup, then point it at the freshly-generated ticket link. Opening externally
    // keeps the operator dashboard intact (no in-app iframe dead-end).
    const w = window.open('', '_blank');
    setLoadingTicketUrl(true);
    try {
      const bookingId = trip.confirmationCode || trip.id;
      const response = await api.post(`/v2/public/bookings/${bookingId}/generate-magic-link`);
      const link = response.data.magicLink;
      if (w) w.location.href = link;
      else window.open(link, '_blank');
    } catch (error) {
      if (w) w.close();
      console.error("Error generating ticket link:", error);
      toast.error("Failed to load ticket");
    } finally {
      setLoadingTicketUrl(false);
    }
  };

  // Helper: Check if teamMember is available for a trip (for dropdown graying)
  const isTeamMemberAvailableForBooking = (teamMember, trip) => {
    // Check 1: Unavailable dates
    if (teamMember.unavailableDates && teamMember.unavailableDates.includes(trip.date)) {
      return false;
    }
    
    // Check 2: Time conflicts
    const tripStart = timeToMinutes(trip.startTime);
    const tripEnd = tripStart + trip.duration;
    
    const sameDayBookings = trips.filter(t => 
      t.date === trip.date && 
      t.id !== trip.id && 
      t.teamMemberId === teamMember.id
    );
    
    for (const existingBooking of sameDayBookings) {
      const existingStart = timeToMinutes(existingBooking.startTime);
      const existingEnd = existingStart + existingBooking.duration;
      
      if (tripStart < existingEnd && tripEnd > existingStart) {
        return false; // Time conflict
      }
    }
    
    return true;
  };

  // Helper: Check if asset is available for a trip (for dropdown graying)
  const isAssetAvailableForBooking = (asset, trip) => {
    // Check time conflicts
    const tripStart = timeToMinutes(trip.startTime);
    const tripEnd = tripStart + trip.duration;
    
    const sameDayBookings = trips.filter(t => 
      t.date === trip.date && 
      t.id !== trip.id && 
      t.assetId === asset.id
    );
    
    for (const existingBooking of sameDayBookings) {
      const existingStart = timeToMinutes(existingBooking.startTime);
      const existingEnd = existingStart + existingBooking.duration;
      
      if (tripStart < existingEnd && tripEnd > existingStart) {
        return false; // Time conflict
      }
    }
    
    return true;
  };

  // Helper: Check if teamMember is allowed on asset
  const isTeamMemberAllowedOnAsset = (teamMemberId, assetId) => {
    if (!assetId || assetId === 'unassigned' || !teamMemberId || teamMemberId === 'unassigned') {
      return true;
    }
    
    const asset = assets.find(b => b.id === assetId);
    if (!asset) return true;
    
    // If asset has no allowedTeamMembers or empty array, all teamMembers allowed
    if (!asset.allowedTeamMembers || asset.allowedTeamMembers.length === 0) {
      return true;
    }
    
    // Check if teamMember is in allowed list
    return asset.allowedTeamMembers.includes(teamMemberId);
  };

  // Helper: Check if asset allows selected teamMember
  const isAssetAllowedForTeamMember = (assetId, teamMemberId) => {
    if (!assetId || assetId === 'unassigned' || !teamMemberId || teamMemberId === 'unassigned') {
      return true;
    }
    
    const asset = assets.find(b => b.id === assetId);
    if (!asset) return true;
    
    // If asset has no allowedTeamMembers or empty array, all teamMembers allowed
    if (!asset.allowedTeamMembers || asset.allowedTeamMembers.length === 0) {
      return true;
    }
    
    // Check if teamMember is in allowed list
    return asset.allowedTeamMembers.includes(teamMemberId);
  };

  // Helper: Check time overlap between two trips
  const checkTimeOverlap = (trip1, trip2) => {
    const start1 = timeToMinutes(trip1.startTime);
    const end1 = start1 + trip1.duration;
    const start2 = timeToMinutes(trip2.startTime);
    const end2 = start2 + trip2.duration;
    
    return start1 < end2 && end1 > start2;
  };

  // Helper: Calculate end time from start time and duration
  const calculateEndTime = (startTime, duration) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = startMinutes + duration;
    const hours = Math.floor(endMinutes / 60) % 24;
    const mins = endMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Conflict detection helper functions
  const checkTeamMemberConflicts = (teamMemberId, trip) => {
    if (!teamMemberId || teamMemberId === 'unassigned') return null;
    
    const teamMember = teamMembers.find(c => c.id === teamMemberId);
    if (!teamMember) return null;
    
    const conflicts = [];
    
    // Check 1: Unavailable dates
    if (teamMember.unavailableDates && teamMember.unavailableDates.includes(trip.date)) {
      conflicts.push({
        type: 'unavailable_date',
        message: `${teamMember.name} has marked ${formatDate(trip.date, dateFormat)} as unavailable`
      });
    }
    
    // Check 2: Time conflicts with other trips
    const tripStart = timeToMinutes(trip.startTime);
    const tripEnd = tripStart + trip.duration;
    
    const sameDayBookings = trips.filter(t => 
      t.date === trip.date && 
      t.id !== trip.id && 
      t.teamMemberId === teamMemberId
    );
    
    for (const existingBooking of sameDayBookings) {
      const existingStart = timeToMinutes(existingBooking.startTime);
      const existingEnd = existingStart + existingBooking.duration;
      
      // Check for overlap (allowing exact adjacent times)
      if (tripStart < existingEnd && tripEnd > existingStart) {
        conflicts.push({
          type: 'time_conflict',
          message: `${teamMember.name} is already assigned to a trip at ${formatTime(existingBooking.startTime, timeFormat)} (${existingBooking.duration} min)`
        });
      }
    }
    
    // Check 3: Double-decker certification
    if (editAssetId && editAssetId !== 'unassigned') {
      const asset = assets.find(b => b.id === editAssetId);
      if (asset && asset.type === 'double-decker' && !teamMember.doubleDeckerCertified) {
        conflicts.push({
          type: 'certification',
          message: `${teamMember.name} is not certified for double-decker assets. Asset: ${asset.name}`
        });
      }
    }
    
    return conflicts.length > 0 ? conflicts : null;
  };

  const checkAssetConflicts = (assetId, trip) => {
    if (!assetId || assetId === 'unassigned') return null;
    
    const asset = assets.find(b => b.id === assetId);
    if (!asset) return null;
    
    const conflicts = [];
    
    // Check time conflicts with other trips
    const tripStart = timeToMinutes(trip.startTime);
    const tripEnd = tripStart + trip.duration;
    
    const sameDayBookings = trips.filter(t => 
      t.date === trip.date && 
      t.id !== trip.id && 
      t.assetId === assetId
    );
    
    for (const existingBooking of sameDayBookings) {
      const existingStart = timeToMinutes(existingBooking.startTime);
      const existingEnd = existingStart + existingBooking.duration;
      
      // Check for overlap (allowing exact adjacent times)
      if (tripStart < existingEnd && tripEnd > existingStart) {
        conflicts.push({
          type: 'time_conflict',
          message: `${asset.name} is already assigned to a trip at ${formatTime(existingBooking.startTime, timeFormat)} (${existingBooking.duration} min)`
        });
      }
    }
    
    return conflicts.length > 0 ? conflicts : null;
  };

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Detect all conflicts across all trips
  const detectAllConflicts = () => {
    const teamMemberConflicts = [];
    const assetConflicts = [];
    
    // Check each trip for conflicts
    trips.forEach(trip => {
      if (trip.teamMemberId && trip.teamMemberId !== 'unassigned') {
        const conflicts = checkTeamMemberConflicts(trip.teamMemberId, trip);
        if (conflicts && conflicts.length > 0) {
          teamMemberConflicts.push(...conflicts.map(c => ({ ...c, tripId: trip.id, date: trip.date })));
        }
      }
      
      if (trip.assetId && trip.assetId !== 'unassigned') {
        const conflicts = checkAssetConflicts(trip.assetId, trip);
        if (conflicts && conflicts.length > 0) {
          assetConflicts.push(...conflicts.map(c => ({ ...c, tripId: trip.id, date: trip.date })));
        }
      }
    });
    
    return { teamMemberConflicts, assetConflicts };
  };

  const handleSaveEdit = async () => {
    try {
      // Check for conflicts before saving
      const teamMemberConflicts = checkTeamMemberConflicts(editTeamMemberId, editingBooking);
      const assetConflicts = checkAssetConflicts(editAssetId, editingBooking);
      
      const allConflicts = [];
      if (teamMemberConflicts) allConflicts.push(...teamMemberConflicts);
      if (assetConflicts) allConflicts.push(...assetConflicts);
      
      // If conflicts exist, show warning and ask for confirmation
      if (allConflicts.length > 0) {
        const conflictMessages = allConflicts.map(c => `Warning: ${c.message}`).join('\n\n');
        const confirmMessage = `${conflictMessages}\n\nAre you sure you want to proceed with this assignment?`;
        
        if (!window.confirm(confirmMessage)) {
          return; // User canceled
        }
      }
      
      await orgApi.updateBooking(editingBooking.id, {
        bookingDate: editingBooking.date,
        startTime: editingBooking.startTime,
        durationMinutes: editingBooking.duration,
        teamMemberId: editTeamMemberId === 'unassigned' ? null : editTeamMemberId,
        assetId: editAssetId === 'unassigned' ? null : editAssetId,
        internalNotes: editingBooking.notes || ''
      });
      
      await fetchData();
      if (onRefresh) onRefresh();
      setEditModalOpen(false);
      toast.success('Assignment updated!');
    } catch (error) {
      console.error('Error updating trip:', error);
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' ? errorDetail?.message : errorDetail;
      toast.error(errorMessage || 'Failed to update trip');
    }
  };

  const handleDayClick = (dateStr) => {
    // Don't open modal in readOnly mode
    if (readOnly) return;
    
    setSelectedDate(dateStr);
    
    // Get trips for this day
    const dayBookings = trips.filter(t => t.date === dateStr);
    setSelectedDayBookings(dayBookings);
    
    // Reset teamMember filter when opening new day
    setSelectedTeamMemberFilters([]);
    
    // Open modal immediately. (Removed a dead /bookings/{id}/availability fetch
    // that 500'd against the legacy Mongo path and whose result was never rendered.)
    setDayModalOpen(true);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  // Get trip status based on assignments
  // Get status badge
  const getStatusBadge = (trip) => {
    const status = getBookingStatus(trip);
    
    if (status === 'assigned') {
      return <Badge className="bg-green-100 text-green-800 text-xs">Assigned</Badge>;
    } else if (status === 'partial') {
      return <Badge className="bg-amber-100 text-amber-800 text-xs">Partial</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-800 text-xs">Unassigned</Badge>;
    }
  };

  // Handle Add Booking
  const handleAddBooking = async (tripData = null) => {
    const data = tripData || newBooking;
    
    if (!data.date || !data.startTime) {
      toast.error('Please fill in date and start time');
      return;
    }

    try {
      const createdBooking = await createManualBooking(data);
      
      // Reset form data
      setNewBooking({
        date: '',
        startTime: '',
        endTime: '',
        duration: '',
        groupSize: '',
        teamMemberId: '',
        assetId: '',
        notes: '',
        teamAssignments: []
      });
      
      // Return the created booking for success modal
      return createdBooking;
    } catch (error) {
      console.error('Error adding trip:', error);
      toast.error('Failed to add trip');
      throw error;
    }
  };

  // Calculate duration helper
  const calculateDuration = (start, end) => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    return totalMinutes;  // Return integer minutes for backend
  };
  
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  const handleHealthCheck = async () => {
    setHealthLoading(true);
    setHealthModalOpen(true);
    
    try {
      const response = await api.get('/health/conflicts', {
        params: { date: format(new Date(), 'yyyy-MM-dd') }
      });
      setHealthData(response.data);
    } catch (error) {
      console.error('Health check error:', error);
      toast.error('Failed to check schedule health');
      setHealthData({ error: 'Failed to load health data' });
    } finally {
      setHealthLoading(false);
    }
  };

  const handleAutoScheduleMonth = async () => {
    try {
      let autoStartDate, autoEndDate;
      
      if (viewMode === 'calendar') {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        autoStartDate = toLocalDateStr(new Date(year, month, 1));
        autoEndDate = toLocalDateStr(new Date(year, month + 1, 0));
      } else {
        // For list/card view, use visible trips to determine date range
        if (trips.length > 0) {
          const dates = trips.map(t => t.date).sort();
          autoStartDate = dates[0];
          autoEndDate = dates[dates.length - 1];
        } else {
          // Default to current month if no trips
          const year = new Date().getFullYear();
          const month = new Date().getMonth();
          autoStartDate = toLocalDateStr(new Date(year, month, 1));
          autoEndDate = toLocalDateStr(new Date(year, month + 1, 0));
        }
      }

      // Get unassigned trips in the date range
      const unassignedBookings = trips.filter(trip => {
        const tripDate = trip.date;
        return tripDate >= autoStartDate && tripDate <= autoEndDate && (!trip.teamMemberId || !trip.assetId);
      });

      // Filter active teamMembers and assets (handle undefined active property)
      const activeTeamMembers = teamMembers.filter(c => c.active !== false);
      const activeAssets = assets.filter(b => b.active !== false);

      // Show confirmation modal with preview
      setSchedulerPreview({
        startDate: autoStartDate,
        endDate: autoEndDate,
        trips: unassignedBookings,
        teamMembers: activeTeamMembers,
        assets: activeAssets
      });
      setShowSchedulerConfirm(true);

    } catch (error) {
      console.error('Error preparing auto-schedule:', error);
      console.error('Error details:', error.stack);
      toast.error(`Failed to prepare auto-schedule: ${error.message}`);
    }
  };

  const executeAutoSchedule = async () => {
    if (!schedulerPreview) return;

    setIsScheduling(true);
    
    try {
      const { startDate, endDate } = schedulerPreview;
      
      const response = await api.post(`/schedule/auto`, { 
        startDate, 
        endDate 
      });
      
      const result = response.data;
      
      // Handle action consumption feedback
      if (result.actions_consumed !== undefined) {
        const { showActionToast } = await import('../utils/actions');
        showActionToast(
          result.actions_consumed, 
          result.actions_remaining, 
          `Auto-scheduled ${result.assignmentsMade} trip${result.assignmentsMade !== 1 ? 's' : ''}`
        );
      }
      
      // Success! Fire confetti!
      
      await fetchData();
      if (onRefresh) onRefresh();
      
      // Store result and show warnings dialog
      setSchedulerResult(result);
      setWarningsDialogOpen(true);
      
      // Close confirmation modal
      setShowSchedulerConfirm(false);
      setIsScheduling(false);
      
    } catch (error) {
      console.error('Error auto-scheduling:', error);
      
      // Handle insufficient actions error (402)
      if (error.response?.status === 402) {
        const { showInsufficientActionsError } = await import('../utils/actions');
        const detail = error.response.data.detail;
        showInsufficientActionsError(detail.required, detail.balance);
      } else {
        toast.error(apiErrorText(error, 'Auto-schedule failed'));
      }
      
      setIsScheduling(false);
    }
  };

  const handleAutoScheduleDay = async () => {
    if (!selectedDate) return;
    
    setAutoScheduling(true);
    const orgId = localStorage.getItem('organizationId');
    
    try {
      // Get unassigned bookings for this day
      const unassignedBookings = selectedDayBookings.filter(
        b => !b.assetId || !b.teamAssignments?.some(a => a.teamId)
      );
      
      if (unassignedBookings.length === 0) {
        toast.info('All bookings are already assigned');
        setAutoScheduling(false);
        return;
      }
      
      let successCount = 0;
      let errorCount = 0;
      
      // Process each unassigned booking with AI
      for (const booking of unassignedBookings) {
        try {
          // Get AI suggestion
          const suggestionRes = await api.post(
            `/v2/organizations/${orgId}/bookings/${booking.id}/ai-schedule-suggestion`
          );
          
          if (suggestionRes.data.success && suggestionRes.data.suggestion?.recommendation) {
            const rec = suggestionRes.data.suggestion.recommendation;
            
            // Apply the suggestion
            const applyRes = await api.post(
              `/v2/organizations/${orgId}/bookings/${booking.id}/apply-schedule`,
              {
                asset_id: rec.asset_id || null,
                team_member_id: rec.team_member_id || null
              }
            );
            
            if (applyRes.data.success) {
              successCount++;
            } else {
              errorCount++;
            }
          } else {
            errorCount++;
          }
        } catch (bookingError) {
          console.error(`Error scheduling booking ${booking.id}:`, bookingError);
          errorCount++;
        }
      }
      
      // Fetch updated data (respects the active payment filter; the trips-change
      // effect refreshes the open day modal automatically)
      await fetchData();
      if (onRefresh) await onRefresh();
      
      // Show result
      if (successCount > 0) {
        toast.success(`AI scheduled ${successCount} booking${successCount !== 1 ? 's' : ''}`);
      }
      if (errorCount > 0) {
        toast.warning(`${errorCount} booking${errorCount !== 1 ? 's' : ''} couldn't be scheduled`);
      }
      
    } catch (error) {
      console.error('Error auto-scheduling day:', error);
      toast.error(apiErrorText(error, 'Auto-scheduling failed'));
    } finally {
      setAutoScheduling(false);
    }
  };

  const handleAssignmentChange = async (tripId, field, value) => {
    try {
      const trip = selectedDayBookings.find(t => t.id === tripId);
      if (!trip) {
        console.error('Booking not found:', tripId);
        toast.error('Booking not found');
        return;
      }

      const updatedBooking = {
        bookingDate: trip.date,
        startTime: trip.startTime,
        durationMinutes: trip.duration,
        teamMemberId: field === 'teamMember' ? (value === 'unassigned' ? null : value) : (trip.teamMemberId || null),
        assetId: field === 'asset' ? (value === 'unassigned' ? null : value) : (trip.assetId || null),
        internalNotes: trip.notes || ''
      };


      await orgApi.updateBooking(tripId, updatedBooking);
      
      // Update local state instead of full refresh (keeps modal open)
      const updates = {
        teamMemberId: updatedBooking.teamMemberId,
        assetId: updatedBooking.assetId
      };
      
      setSelectedDayBookings(prevBookings => 
        prevBookings.map(t => t.id === tripId ? { ...t, ...updates } : t)
      );
      
      setBookings(prevBookings => 
        prevBookings.map(t => t.id === tripId ? { ...t, ...updates } : t)
      );
      
      // Don't call fetchData() to prevent modal from closing
    } catch (error) {
      console.error('Error updating assignment:', error);
      console.error('Error details:', error.response?.data);
      toast.error(apiErrorText(error, 'Failed to update assignment'));
    }
  };

  // Generic trip update handler (for team assignments and other fields)
  const handleUpdateBooking = async (tripId, updates) => {
    try {
      const trip = selectedDayBookings.find(t => t.id === tripId);
      if (!trip) {
        console.error('Booking not found:', tripId);
        toast.error('Booking not found');
        return;
      }

      // Update local state immediately for responsiveness
      setSelectedDayBookings(prevBookings => 
        prevBookings.map(t => t.id === tripId ? { ...t, ...updates } : t)
      );
      setBookings(prevBookings => 
        prevBookings.map(t => t.id === tripId ? { ...t, ...updates } : t)
      );

      // Clear existing timeout for this trip
      if (updateTimeouts[tripId]) {
        clearTimeout(updateTimeouts[tripId]);
      }

      // Debounce the API call
      const timeoutId = setTimeout(async () => {
        try {
          const updatedBooking = {
            bookingDate: trip.date,
            startTime: trip.startTime,
            durationMinutes: trip.duration,
            internalNotes: trip.notes || '',
            ...updates,  // Apply updates FIRST
            // Then fill in missing fields from current trip state
            teamMemberId: updates.teamMemberId !== undefined ? updates.teamMemberId : (trip.teamMemberId || null),
            assetId: updates.assetId !== undefined ? updates.assetId : (trip.assetId || null)
          };


          await orgApi.updateBooking(tripId, updatedBooking);
          
          toast.success('Saved!', { duration: 1500 });
        } catch (error) {
          console.error('Error saving trip:', error);
          toast.error('Failed to save changes');
          // Revert local state on error
          onRefresh();
        }
      }, 1000); // 1 second debounce

      setUpdateTimeouts(prev => ({ ...prev, [tripId]: timeoutId }));

    } catch (error) {
      console.error('Error updating trip:', error);
      toast.error('Update failed');
    }
  };

  // Mass actions handlers
  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    if (massActionMode) {
      // Clear selections when turning off mass action mode
      setSelectedBookingIds([]);
    }
  };

  const handleSelectAll = () => {
    const visibleBookings = getSortedBookings();
    if (selectedBookingIds.length === visibleBookings.length) {
      setSelectedBookingIds([]);
    } else {
      setSelectedBookingIds(visibleBookings.map(t => t.id));
    }
  };

  // Handle contact click - show modal instead of direct call/email
  const handleContact = ({ type, value, name, booking }) => {
    setContactModal({ open: true, type, value, name, booking });
  };

  // Handle resending confirmation
  const handleResendConfirmation = async (resendType) => {
    if (!contactModal.booking) return;
    
    setResendingConfirmation(true);
    try {
      const response = await api.post(
        `/v2/organizations/${contactModal.booking.organization_id || contactModal.booking.organizationId}/bookings/${contactModal.booking.id}/resend-confirmation`,
        { type: resendType }
      );
      
      if (response.data.success) {
        toast.success(`Confirmation ${resendType === 'email' ? 'email' : 'SMS'} sent!`);
        setContactModal({ open: false, type: null, value: null, name: null, booking: null });
      } else {
        toast.error(response.data.message || 'Failed to resend');
      }
    } catch (err) {
      console.error('Resend error:', err);
      toast.error(apiErrorText(err, 'Failed to resend confirmation'));
    } finally {
      setResendingConfirmation(false);
    }
  };

  const handleSelectBooking = (tripId) => {
    setSelectedBookingIds(prev => {
      if (prev.includes(tripId)) {
        return prev.filter(id => id !== tripId);
      } else {
        return [...prev, tripId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedBookingIds.length === 0) {
      toast.error('No trips selected');
      return;
    }

    try {
      await Promise.all(
        selectedBookingIds.map(tripId => 
          orgApi.cancelBooking(tripId)
        )
      );

      toast.success(`Successfully deleted ${selectedBookingIds.length} trip(s)`);
      setSelectedBookingIds([]);
      setShowDeleteConfirm(false);
      await fetchData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting trips:', error);
      toast.error('Failed to delete trips');
    }
  };

  const handleDeleteSingleBooking = async (tripId) => {
    try {
      await orgApi.cancelBooking(tripId);
      toast.success('Booking deleted successfully');
      await fetchData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error('Failed to delete trip');
    }
  };

  // Generate payment link for "Pay Later" bookings
  const handleGeneratePaymentLink = async (booking) => {
    setCollectingBookingId(booking.id);
    setGeneratingPaymentLink(true);
    try {
      const response = await api.post(`/admin/bookings/${booking.id}/generate-payment-link`);
      
      if (response.data.success) {
        setPaymentLinkData({
          checkoutUrl: response.data.checkoutUrl,
          customerEmail: booking.customerEmail,
          customerPhone: booking.customerPhone,
          bookingDetails: {
            serviceName: booking.serviceName,
            preferredDate: booking.date || booking.preferredDate || booking.bookingDate,
            preferredTime: booking.startTime || booking.preferredTime,
            customerName: booking.customerName,
            totalAmount: response.data.amount,
            currency: response.data.currency || booking.currency || 'USD'
          }
        });
        setShowPaymentLinkModal(true);
        toast.success('Payment link generated!');
      } else {
        throw new Error(response.data.message || 'Failed to generate payment link');
      }
    } catch (error) {
      console.error('Error generating payment link:', error);
      toast.error(apiErrorText(error, 'Failed to generate payment link'));
    } finally {
      setGeneratingPaymentLink(false);
      setCollectingBookingId(null);
    }
  };

  const handleAddBookingFromCalendar = () => {
    setAddBookingModalOpen(true);
  };

  const getAvailableTeamMembers = () => {
    if (!selectedDate) return [];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const assignedTeamMemberIds = dayBookings.map(t => t.teamMemberId).filter(Boolean);
    
    return teamMembers.filter(c => !assignedTeamMemberIds.includes(c.id));
  };

  const getWorkingTeamMembers = () => {
    if (!selectedDate) return [];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const assignedTeamMemberIds = [...new Set(dayBookings.map(t => t.teamMemberId).filter(Boolean))];
    
    return teamMembers.filter(c => assignedTeamMemberIds.includes(c.id));
  };

  const getAvailableAssets = () => {
    if (!selectedDate) return [];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const assignedAssetIds = dayBookings.map(t => t.assetId).filter(Boolean);
    
    return assets.filter(b => b.active && !assignedAssetIds.includes(b.id));
  };

  const getInUseAssets = () => {
    if (!selectedDate) return [];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const assignedAssetIds = [...new Set(dayBookings.map(t => t.assetId).filter(Boolean))];
    
    return assets.filter(b => assignedAssetIds.includes(b.id));
  };

  const getTeamMemberName = (teamMemberId) => {
    const teamMember = teamMembers.find(c => c.id === teamMemberId);
    return teamMember ? teamMember.name : 'Unassigned';
  };

  const getAssetName = (assetId) => {
    const asset = assets.find(b => b.id === assetId);
    return asset ? asset.name : 'Unassigned';
  };

  const getAssetType = (assetId) => {
    const asset = assets.find(b => b.id === assetId);
    return asset ? asset.type : '';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-3 h-3 ml-1 inline" /> : 
      <ArrowDown className="w-3 h-3 ml-1 inline" />;
  };

  const getSortedBookings = () => {
    const filteredBookings = getFilteredBookings();
    return [...filteredBookings].sort((a, b) => {
      let compareValue = 0;
      
      switch (sortField) {
        case 'date':
          compareValue = a.date.localeCompare(b.date);
          if (compareValue === 0) {
            compareValue = a.startTime.localeCompare(b.startTime);
          }
          break;
        case 'time':
          compareValue = a.startTime.localeCompare(b.startTime);
          break;
        case 'teamMember':
          compareValue = getTeamMemberName(a.teamMemberId).localeCompare(getTeamMemberName(b.teamMemberId));
          break;
        case 'asset':
          compareValue = getAssetName(a.assetId).localeCompare(getAssetName(b.assetId));
          break;
        case 'status':
          compareValue = a.status.localeCompare(b.status);
          break;
        default:
          compareValue = 0;
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
  };

  // Render calendar grid
  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    
    // Use local date for "today" comparison to avoid timezone issues
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="aspect-square p-0.5">
          <div className="w-full h-full rounded-lg bg-gray-50/30"></div>
        </div>
      );
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const status = getDateStatus(dateStr);
      const tripCount = getBookingsForDate(dateStr).length;
      const isToday = dateStr === todayStr;
      const isPast = dateStr < todayStr;
      
      // Determine cell styling based on status, today, and past
      const getCellStyles = () => {
        // Past days are greyed out
        if (isPast) {
          if (tripCount > 0) {
            // Past day with bookings - muted blue
            return 'bg-blue-50/50 border border-blue-200 opacity-60';
          }
          return 'bg-gray-50 border border-gray-100 opacity-40';
        }
        
        // Today
        if (isToday) {
          return 'bg-blue-600 text-white shadow-md hover:bg-blue-700 ring-2 ring-blue-300';
        }
        
        // Future/current days with bookings — all blue
        if (tripCount > 0) {
          return 'bg-blue-50 border border-blue-300 hover:border-blue-400 hover:bg-blue-100';
        }
        return 'bg-white border border-gray-200 hover:border-gray-300 hover:bg-gray-50';
      };
      
      days.push(
        <div key={day} className="aspect-square p-0.5">
          <div
            onClick={() => handleDayClick(dateStr)}
            className={`w-full h-full rounded-lg flex flex-col items-center justify-center cursor-pointer transition-all duration-150 ${getCellStyles()}`}
          >
            {/* Day number */}
            <span className={`text-sm ${
              isToday ? 'text-white font-medium' : 
              isPast ? 'text-gray-400' : 
              'text-gray-700'
            }`}>
              {day}
            </span>
            
            {/* Trip count badge */}
            {tripCount > 0 && (
              <div className={`mt-0.5 flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-medium ${
                isToday 
                  ? 'bg-white/25 text-white' 
                  : isPast
                    ? 'bg-gray-200 text-gray-500'
                    : 'bg-blue-200 text-blue-700'
              }`}>
                {tripCount}
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return days;
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <TabPageLayout
      title={readOnly ? 'Schedule' : 'Bookings'}
      subtitle={readOnly ? undefined : 'Manage your bookings'}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          {/* "See My Bookings" Toggle for TeamMember View */}
          {showMyBookingsToggle && (
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-[0_8px_24px_rgba(0,0,0,0.04)] border border-gray-100">
              <input
                type="checkbox"
                id="myBookingsToggle"
                checked={showMyBookingsOnly}
                onChange={(e) => setShowMyBookingsOnly(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="myBookingsToggle" className="text-sm font-medium text-gray-700 cursor-pointer">
                See My Bookings Only
              </label>
            </div>
          )}
          
          {/* Create Booking Button */}
          {!readOnly && (
            <LayoutActionButton
              icon={Plus}
              onClick={() => setAddBookingModalOpen(true)}
              data-testid="create-booking-btn"
            >
              Create
            </LayoutActionButton>
          )}
        </div>
      }
    >
      <div className="space-y-4">

      {/* Payment Filter Pills — multi-select (OR) */}
      <MultiChipTabs
        value={paymentFilter}
        onChange={(arr) => {
          setPaymentFilter(arr);
          fetchData(arr); // call directly — don't wait for effect re-run
        }}
        testIdPrefix="booking-filter"
        removable
        tabs={[
          { value: 'paid',      label: 'Paid' },
          { value: 'deposit',   label: 'Security Deposit' },
          { value: 'partial',   label: 'Partial Paid' },
          { value: 'unpaid',    label: 'Unpaid' },
          { value: 'refunded',  label: 'Refunded' },
          { value: 'cancelled', label: 'Cancelled' },
        ]}
      />

      {/* Channel show/hide — front-end only, never touches the integrations */}
      <SourceFilter enabled={sourceKeys} onToggle={toggleSource} className="-mt-1" />
      {(viewMode === 'list' || viewMode === 'kanban') && (
        <div className="bg-white rounded-lg p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)] space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-700">Date Range:</span>
              <div className="flex gap-2">
                <Button
                  variant={dateRangePreset === 'today' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyDateRangePreset('today')}
                  style={dateRangePreset === 'today' ? { background: '#2563EB', color: 'white' } : {}}
                >
                  Today
                </Button>
                <Button
                  variant={dateRangePreset === 'next7' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyDateRangePreset('next7')}
                  style={dateRangePreset === 'next7' ? { background: '#2563EB', color: 'white' } : {}}
                >
                  Next 7 Days
                </Button>
                <Button
                  variant={dateRangePreset === 'next30' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyDateRangePreset('next30')}
                  style={dateRangePreset === 'next30' ? { background: '#2563EB', color: 'white' } : {}}
                >
                  Next 30 Days
                </Button>
                <Button
                  variant={dateRangePreset === 'custom' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => applyDateRangePreset('custom')}
                  style={dateRangePreset === 'custom' ? { background: '#2563EB', color: 'white' } : {}}
                >
                  Custom
                </Button>
              </div>
            </div>
            {/* Smart Schedule Button for List View */}
            {!readOnly && getUnscheduledCount() > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      onClick={() => setSmartScheduleOpen(true)}
                      variant="ghost"
                      size="sm"
                      className="h-8 px-2.5 flex items-center gap-1.5 hover:bg-purple-50 text-purple-600 border border-purple-200"
                      data-testid="smart-schedule-btn-list"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span className="text-sm font-medium">Smart</span>
                      <span className="bg-purple-100 text-purple-700 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                        {getUnscheduledCount()}
                      </span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    <div className="text-xs">
                      {(() => {
                        const breakdown = getUnscheduledBreakdown();
                        return (
                          <>
                            {breakdown.needsAsset > 0 && <p>{breakdown.needsAsset} need asset</p>}
                            {breakdown.needsTeam > 0 && <p>{breakdown.needsTeam} need team</p>}
                            {breakdown.needsBoth > 0 && <p>{breakdown.needsBoth} need both</p>}
                          </>
                        );
                      })()}
                    </div>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          
          {/* Custom Date Picker */}
          {showCustomDatePicker && (
            <div className="flex items-end gap-3 pt-2 border-t">
              <div className="flex-1">
                <Label className="text-xs text-gray-600">Start Date</Label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-gray-600">End Date</Label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                />
              </div>
              <Button
                size="sm"
                onClick={handleCustomDateApply}
                style={{ background: '#2563EB' }}
                className="text-white"
              >
                Apply Range
              </Button>
            </div>
          )}
          
          {/* Display current range */}
          {!showCustomDatePicker && startDate && endDate && (
            <div className="text-sm text-gray-600">
              Showing trips from <span className="font-semibold">{formatDate(startDate, dateFormat)}</span> to <span className="font-semibold">{formatDate(endDate, dateFormat)}</span>
            </div>
          )}
        </div>
      )}

      {/* Contact Modal - Using Dialog for proper focus management */}
      <Dialog open={contactModal.open} onOpenChange={(open) => !open && setContactModal({ open: false, type: null, value: null, name: null, booking: null })}>
        <DialogContent className="w-72 p-0 rounded-2xl border-0 shadow-xl">
          <div className="py-8 px-6">
            <div className="text-center">
              <div className={`w-14 h-14 rounded-full mx-auto mb-5 flex items-center justify-center ${
                contactModal.type === 'phone' ? 'bg-green-50' : 'bg-blue-50'
              }`}>
                {contactModal.type === 'phone' ? (
                  <Phone className="w-6 h-6 text-green-500" strokeWidth={1.5} />
                ) : (
                  <Mail className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
                )}
              </div>
              <p className="text-sm text-gray-400 font-light mb-1">{contactModal.name}</p>
              <p className="text-lg font-medium text-gray-900 mb-6 select-all tracking-tight">
                {contactModal.value}
              </p>
              <div className="flex flex-col gap-2">
                <Button
                  className={`w-full h-11 text-white font-medium ${
                    contactModal.type === 'phone' 
                      ? 'bg-green-500 hover:bg-green-600' 
                      : 'bg-blue-500 hover:bg-blue-600'
                  }`}
                  onClick={() => {
                    if (contactModal.type === 'phone') {
                      window.location.href = `tel:${contactModal.value}`;
                    } else {
                      window.location.href = `mailto:${contactModal.value}`;
                    }
                  }}
                >
                  {contactModal.type === 'phone' ? 'Call' : 'Send Email'}
                </Button>
                
                {/* Resend Confirmation Button */}
                {contactModal.booking && (
                  <Button
                    variant="outline"
                    className="w-full h-11 font-normal border-gray-200 hover:bg-gray-50"
                    disabled={resendingConfirmation}
                    onClick={() => handleResendConfirmation(contactModal.type === 'phone' ? 'sms' : 'email')}
                  >
                    {resendingConfirmation ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin"></span>
                        Sending...
                      </span>
                    ) : (
                      `Resend Booking ${contactModal.type === 'phone' ? 'SMS' : 'Email'}`
                    )}
                  </Button>
                )}
                
                <Button
                  variant="ghost"
                  className="w-full h-11 text-gray-500 font-normal hover:bg-gray-50"
                  onClick={() => {
                    navigator.clipboard.writeText(contactModal.value);
                    toast.success('Copied to clipboard');
                  }}
                >
                  Copy to Clipboard
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Grid */}
      {viewMode === 'calendar' && (
        <>
          <Card>
            <CardContent className="p-4">
              {/* Month Navigation Header — month stays centered; Today floats right */}
              <div className="relative flex items-center justify-center mb-4 min-h-[36px]">
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={handlePrevMonth} className="h-8 w-8 p-0">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <span className="text-lg font-light tracking-tight text-gray-900 min-w-[160px] text-center">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  <Button variant="ghost" size="sm" onClick={handleNextMonth} className="h-8 w-8 p-0">
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </div>
                {/* Today button - only show if NOT viewing current month; absolute so it never shifts the centered month */}
                {(currentMonth.getMonth() !== new Date().getMonth() || 
                  currentMonth.getFullYear() !== new Date().getFullYear()) && (
                  <div className="absolute right-0">
                    <Button variant="outline" size="sm" onClick={handleToday}>
                      Today
                    </Button>
                  </div>
                )}
              </div>
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center text-xs sm:text-sm font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {renderCalendar()}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Card View - Upcoming/Completed */}
      {viewMode === 'card' && (
        <div className="space-y-4">
          {/* Tabs: Upcoming / Completed */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCardViewTab('upcoming')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                cardViewTab === 'upcoming'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Upcoming
            </button>
            <button
              onClick={() => setCardViewTab('completed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                cardViewTab === 'completed'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              Completed
            </button>
          </div>

          {/* Booking Cards */}
          {(() => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            // DATE PARSING RULE — DO NOT CHANGE
            // booking_date arrives from the API as a plain "YYYY-MM-DD" string (PostgreSQL DATE, no timezone).
            // new Date("2026-02-15")          → JS parses as UTC midnight → shows Feb 14 in US timezones. WRONG.
            // new Date("2026-02-15T00:00:00") → JS parses as LOCAL midnight → shows Feb 15 everywhere. CORRECT.
            // Always append 'T00:00:00' (no Z) when constructing a Date from a YYYY-MM-DD string.
            
            // Filter and sort bookings based on tab
            const filteredBookings = trips.filter(booking => {
              const dateStr = booking.bookingDate || booking.booking_date;
              const bookingDate = dateStr ? new Date(dateStr + 'T00:00:00') : new Date(0);
              bookingDate.setHours(0, 0, 0, 0);
              
              if (cardViewTab === 'upcoming') {
                return bookingDate >= today;
              } else {
                return bookingDate < today;
              }
            }).sort((a, b) => {
              const aStr = a.bookingDate || a.booking_date;
              const bStr = b.bookingDate || b.booking_date;
              const dateA = aStr ? new Date(aStr + 'T00:00:00') : new Date(0);
              const dateB = bStr ? new Date(bStr + 'T00:00:00') : new Date(0);
              
              if (cardViewTab === 'upcoming') {
                // Upcoming: soonest first (ascending)
                return dateA - dateB;
              } else {
                // Completed: most recent first (descending)
                return dateB - dateA;
              }
            }).slice(0, 50); // Limit to 50 for pagination
            
            if (filteredBookings.length === 0) {
              return (
                <Card>
                  <CardContent className="py-12">
                    <div className="text-center text-gray-500">
                      <CalendarDays className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-lg font-medium">
                        {cardViewTab === 'upcoming' ? 'No upcoming bookings' : 'No completed bookings'}
                      </p>
                      <p className="text-sm mt-1">
                        {cardViewTab === 'upcoming' 
                          ? 'Bookings will appear here once they are scheduled'
                          : 'Past bookings will appear here'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            }
            
            // Group bookings by date for better organization
            const groupedByDate = filteredBookings.reduce((acc, booking) => {
              const dateStr = booking.bookingDate || booking.booking_date;
              if (!acc[dateStr]) {
                acc[dateStr] = [];
              }
              acc[dateStr].push(booking);
              return acc;
            }, {});
            
            return (
              <div className="space-y-6">
                {Object.entries(groupedByDate).map(([dateStr, bookings]) => {
                  const date = new Date(dateStr + 'T00:00:00');
                  const isToday = date.toDateString() === new Date().toDateString();
                  
                  return (
                    <div key={dateStr}>
                      {/* Date Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`px-3 py-1.5 rounded-lg ${isToday ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                          <span className="text-sm font-semibold">
                            {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <span className="text-sm text-gray-500">
                          {bookings.length} booking{bookings.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      
                      {/* Booking Cards for this date */}
                      <div className="space-y-3">
                        {bookings.map((booking, idx) => (
                          <BookingCard
                            key={booking.id}
                            booking={booking}
                            slotColor={['#3B82F6', '#8B5CF6', '#14B8A6', '#F97316'][idx % 4]}
                            showEditButton={!readOnly}
                            onEdit={(b) => {
                              setEditingBooking(b);
                              setEditModalOpen(true);
                            }}
                            onContact={handleContact}
                          />
                        ))}
                      </div>
                    </div>
                  );
                })}
                
                {/* Show count */}
                <div className="text-center text-sm text-gray-500 pt-4">
                  Showing {filteredBookings.length} of {trips.filter(b => {
                    const bDateStr = b.bookingDate || b.booking_date;
                    const bookingDate = bDateStr ? new Date(bDateStr + 'T00:00:00') : new Date(0);
                    bookingDate.setHours(0, 0, 0, 0);
                    return cardViewTab === 'upcoming' ? bookingDate >= today : bookingDate < today;
                  }).length} {cardViewTab} bookings
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {getSortedBookings().length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No trips scheduled</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mass Actions Toolbar */}
              {!readOnly && selectedBookingIds.length > 0 && (
                <MassActionToolbar
                  selectedCount={selectedBookingIds.length}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={() => setSelectedBookingIds([])}
                  onDelete={() => setShowDeleteConfirm(true)}
                  itemName="trip"
                />
              )}

              <div className="bg-white rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      {!readOnly && massActionMode && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12 sticky left-0 bg-gray-50">
                          <input
                            type="checkbox"
                            checked={selectedBookingIds.length === getSortedBookings().length && getSortedBookings().length > 0}
                            onChange={handleSelectAll}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        </th>
                      )}
                      <th 
                        onClick={() => handleSort('date')}
                        className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                      >
                        When {getSortIcon('date')}
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Customer
                      </th>
                      <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Service
                      </th>
                      <th className="hidden lg:table-cell px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        Guests
                      </th>
                      <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        <span className="hidden sm:inline">$</span> Status
                      </th>
                      <th 
                        onClick={() => handleSort('status')}
                        className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors whitespace-nowrap"
                      >
                        Assignment {getSortIcon('status')}
                      </th>
                      {!readOnly && (
                        <th className="px-2 md:px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap sticky right-0 bg-gray-50 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                          <span className="hidden sm:inline">Actions</span>
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {getSortedBookings().map((trip) => {
                      const asset = assets?.find(b => b.id === trip.assetId);
                      const assignedTeam = trip.teamAssignments?.filter(a => a.teamId) || [];
                      
                      // Payment calculations
                      const total = trip.totalAmount || trip.totalPrice || trip.servicePrice || 0;
                      const paid = trip.amountPaid || trip.amount_paid || 0;
                      const balance = total - paid;
                      let paymentStatus = trip.paymentStatus;
                      if (paid > 0 && balance > 0) paymentStatus = 'partial';
                      else if (paid >= total && total > 0) paymentStatus = 'paid';
                      
                      // Assignment status
                      const assignmentStatus = getBookingStatus(trip);
                      
                      // Clean row style - no colored backgrounds
                      const rowBgClass = 'hover:bg-gray-50';
                      
                      // Handle row click to open edit modal
                      const handleRowClick = (e) => {
                        // Don't trigger if clicking on buttons, inputs, or links
                        if (e.target.closest('button') || e.target.closest('input') || e.target.closest('a')) {
                          return;
                        }
                        // Open edit modal instead of magic link
                        setEditingBooking(trip);
                        setEditModalOpen(true);
                      };
                      
                      // Copy to clipboard helper
                      const copyToClipboard = async (text, label) => {
                        try {
                          await navigator.clipboard.writeText(text);
                          toast.success(`${label} copied!`);
                        } catch (err) {
                          toast.error('Failed to copy');
                        }
                      };
                      
                      return (
                      <tr 
                        key={trip.id} 
                        className={`${rowBgClass} cursor-pointer transition-colors border-b border-gray-100`}
                        onClick={handleRowClick}
                      >
                        {!readOnly && massActionMode && (
                          <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-inherit">
                            <input
                              type="checkbox"
                              checked={selectedBookingIds.includes(trip.id)}
                              onChange={() => handleSelectBooking(trip.id)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </td>
                        )}
                        {/* When = Date + Time combined */}
                        <td className="px-3 md:px-4 py-3 whitespace-nowrap">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900">
                              {formatDatePretty(trip.preferredDate || trip.date)}
                            </div>
                            <div className="text-gray-500 text-xs">
                              {formatTime(trip.startTime || trip.preferredTime, timeFormat)}
                              {(trip.timezone || orgTimezone) && (
                                <span className="text-gray-400 ml-1 hidden sm:inline">
                                  {getTimezoneAbbr(trip.timezone || orgTimezone)}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        {/* Customer with phone/email shown */}
                        <td className="px-3 md:px-4 py-3 max-w-[160px] md:max-w-[220px]">
                          {trip.customerName ? (
                            <div className="text-sm">
                              <div className="font-medium text-gray-900 truncate">
                                {formatCustomerName(trip.customerName)}
                                <span className="text-[10px] text-gray-400 font-mono ml-1.5 hidden sm:inline">
                                  #{trip.confirmationCode}
                                </span>
                              </div>
                              <div className="flex flex-col gap-0.5 mt-0.5">
                                {trip.customerPhone && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(trip.customerPhone, 'Phone');
                                    }}
                                    className="text-xs text-gray-500 hover:text-blue-600 flex items-center gap-1 transition-colors text-left"
                                    title="Click to copy phone"
                                  >
                                    <Phone className="w-3 h-3 flex-shrink-0" />
                                    <span className="truncate max-w-[100px] md:max-w-none">{trip.customerPhone}</span>
                                  </button>
                                )}
                                {trip.customerEmail && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      copyToClipboard(trip.customerEmail, 'Email');
                                    }}
                                    className="hidden md:flex text-xs text-gray-500 hover:text-blue-600 items-center gap-1 transition-colors text-left"
                                    title="Click to copy email"
                                  >
                                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    <span className="truncate">{trip.customerEmail}</span>
                                  </button>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        {/* Service + Add-ons - hidden on mobile */}
                        <td className="hidden md:table-cell px-4 py-3 max-w-[180px]">
                          <div className="text-sm text-gray-900 truncate font-medium">{trip.serviceName || '-'}</div>
                          {trip.selectedAddOns && trip.selectedAddOns.length > 0 && (
                            <div className="text-xs text-gray-500 truncate mt-0.5">
                              {trip.selectedAddOns.map(a => a.name || a.addOnName).join(', ')}
                            </div>
                          )}
                          {asset && !trip.selectedAddOns?.length && (
                            <div className="text-xs text-gray-500 truncate mt-0.5">{asset.name}</div>
                          )}
                        </td>
                        {/* Guests (party size) - hidden on small screens */}
                        <td className="hidden lg:table-cell px-4 py-3 whitespace-nowrap text-center">
                          <span className="text-sm text-gray-900">{trip.partySize || trip.groupSize || 1}</span>
                        </td>
                        {/* $ Status = Amount Due + Paid + Collect */}
                        <td className="px-3 md:px-4 py-3 whitespace-nowrap">
                          <div className="flex flex-col gap-0.5">
                            {/* Due and Paid amounts */}
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500">Due:</span>
                              <span className="font-semibold text-gray-900">${total.toFixed(2)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-500">Paid:</span>
                              <span className={`font-semibold ${
                                paid >= total && total > 0 ? 'text-green-600' :
                                paid > 0 ? 'text-orange-600' : 'text-gray-400'
                              }`}>
                                ${paid.toFixed(2)}
                              </span>
                              {paid > 0 && paid < total && (
                                <span className="text-[10px] px-1 py-0.5 bg-orange-100 text-orange-700 rounded">
                                  ${balance.toFixed(2)} left
                                </span>
                              )}
                            </div>
                            {/* Collect button for unpaid */}
                            {paymentStatus !== 'paid' && total > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGeneratePaymentLink(trip);
                                }}
                                disabled={collectingBookingId === trip.id}
                                className="text-[10px] md:text-xs text-orange-600 hover:text-orange-700 font-medium hover:underline flex items-center gap-1 disabled:opacity-50 mt-0.5"
                              >
                                {collectingBookingId === trip.id ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <>
                                    <CreditCard className="w-3 h-3" />
                                    <span className="hidden sm:inline">Collect</span>
                                  </>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                        {/* Assignment Status - hidden on mobile */}
                        <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap">
                          <div className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded ${
                            assignmentStatus === 'assigned' 
                              ? 'bg-green-50 text-green-700' 
                              : assignmentStatus === 'partial'
                              ? 'bg-amber-50 text-amber-700'
                              : 'bg-red-50 text-red-600'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${
                              assignmentStatus === 'assigned' ? 'bg-green-500' :
                              assignmentStatus === 'partial' ? 'bg-amber-500' : 'bg-red-500'
                            }`}></div>
                            {assignmentStatus === 'assigned' ? 'Ready' : 
                             assignmentStatus === 'partial' ? 'Needs Staff' : 'Unassigned'}
                          </div>
                        </td>
                        {/* Actions - Single Edit button on mobile, icons on desktop */}
                        {!readOnly && (
                          <td className="px-2 md:px-4 py-2 md:py-3 whitespace-nowrap text-right sticky right-0 bg-white shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
                            {/* Desktop: Icon buttons */}
                            <div className="hidden sm:flex items-center justify-end gap-0.5">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(trip);
                                }}
                                className="h-7 w-7 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                title="Edit booking"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewTicket(trip);
                                }}
                                disabled={loadingTicketUrl}
                                className="h-7 w-7 p-0 text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                                title="View ticket"
                              >
                                {loadingTicketUrl ? (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : (
                                  <Eye className="w-3.5 h-3.5" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setBookingToDelete(trip);
                                  setShowDeleteConfirm(true);
                                }}
                                className="h-7 w-7 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50"
                                title="Delete booking"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                            {/* Mobile: Just Edit button - clicking row opens details */}
                            <div className="sm:hidden">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditClick(trip);
                                }}
                                className="h-8 w-8 p-0 text-gray-500"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <KanbanView
          bookings={getSortedBookings()}
          assets={assets}
          teamMembers={teamMembers}
          roles={roles}
          onEdit={(booking) => {
            setEditingBooking(booking);
            setEditModalOpen(true);
          }}
          onView={(booking) => handleViewTicket(booking)}
          onCheckIn={async (bookingId, checkedIn) => {
            try {
              await orgApi.updateBooking(bookingId, { checkedIn });
              await fetchData();
              toast.success(checkedIn ? 'Checked in!' : 'Check-in removed');
            } catch (error) {
              console.error('Check-in error:', error);
              toast.error('Failed to update check-in status');
            }
          }}
          timeFormat={timeFormat}
          readOnly={readOnly}
        />
      )}

      </div>
      {/* End of space-y-4 wrapper */}

      {/* Day Detail Modal (for Calendar view) - fixed-height flex column so the header/close stay pinned on mobile */}
      <Dialog open={dayModalOpen} onOpenChange={setDayModalOpen}>
        <DialogContent
          className="max-w-xl p-0 gap-0 overflow-hidden flex flex-col"
          data-testid="day-detail-modal"
        >
          {/* Swipe-down handle (mobile only) */}
          <div
            className="sm:hidden flex-none pt-2.5 pb-1 flex justify-center touch-none cursor-grab active:cursor-grabbing"
            onTouchStart={handleSheetTouchStart}
            onTouchEnd={handleSheetTouchEnd}
            data-testid="day-modal-swipe-handle"
          >
            <div className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>

          {/* Pinned header */}
          <DialogHeader className="flex-none px-6 pt-4 sm:pt-6 pb-3 border-b border-gray-100">
            <DialogTitle className="text-lg pr-8">
              {selectedDate && formatDate(selectedDate, dateFormat)}
              <span className="text-sm font-normal text-gray-500 ml-2">
                ({selectedDayBookings.length} booking{selectedDayBookings.length !== 1 ? 's' : ''})
              </span>
            </DialogTitle>
          </DialogHeader>

          {/* Scrollable body */}
          <div
            className="flex-1 min-h-0 overflow-y-auto px-6 py-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            {/* Bookings for the day */}
            {selectedDayBookings.length === 0 ? (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                <p className="text-gray-500">No trips scheduled</p>
              </div>
            ) : (
              <div className="space-y-3 max-sm:scale-[0.9] max-sm:origin-top">
                {selectedDayBookings.map((trip, idx) => (
                  <BookingCard
                    key={trip.id}
                    booking={trip}
                    slotColor={['#3B82F6', '#8B5CF6', '#14B8A6', '#F97316'][idx % 4]}
                    showEditButton={!readOnly}
                    onEdit={(booking) => {
                      setEditingBooking(booking);
                      setEditModalOpen(true);
                      setDayModalOpen(false);
                    }}
                    onContact={handleContact}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Pinned footer Close button (always reachable on mobile) */}
          <div
            className="flex-none px-6 py-3 border-t border-gray-100 bg-white"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={() => setDayModalOpen(false)}
              data-testid="day-modal-close-btn"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Scheduler Warnings Dialog */}
      <AutoSchedulerWarningsDialog
        open={warningsDialogOpen}
        onOpenChange={setWarningsDialogOpen}
        result={schedulerResult}
      />

      {/* Add Booking Wizard - Clean 3-Step Flow */}
      {addBookingModalOpen && (
        <AddBookingWizard
          assets={assets}
          teamMembers={teamMembers}
          roles={roles}
          onComplete={async (tripData) => {
            const result = await handleAddBooking(tripData);
            return result; // Return booking data - modal will be closed by success modal
          }}
          onCancel={() => setAddBookingModalOpen(false)}
        />
      )}

      {/* Mass Actions Toolbar */}
      {massActionMode && (
        <MassActionToolbar
          selectedCount={selectedBookingIds.length}
          onSelectAll={handleSelectAll}
          onClearSelection={() => setSelectedBookingIds([])}
          onBulkDelete={() => setShowDeleteConfirm(true)}
          onCancel={() => setMassActionMode(false)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              {tripToDelete 
                ? `Are you sure you want to delete this booking? This action cannot be undone.`
                : `Are you sure you want to delete ${selectedBookingIds.length} booking(s)? This action cannot be undone.`
              }
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => {
              if (tripToDelete) {
                handleDeleteSingleBooking(tripToDelete.id);
                setBookingToDelete(null);
              } else {
                handleBulkDelete();
              }
              setShowDeleteConfirm(false);
            }}>
              Delete {tripToDelete ? 'Booking' : `${selectedBookingIds.length} Booking(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Scheduler Confirmation Modal */}
      <Dialog open={showSchedulerConfirm} onOpenChange={setShowSchedulerConfirm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Auto-Schedule Preview
            </DialogTitle>
            <DialogDescription>
              Review what will be scheduled automatically
            </DialogDescription>
          </DialogHeader>
          
          {schedulerPreview && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Date Range</p>
                  <p className="text-lg font-semibold">
                    {formatDatePretty(schedulerPreview.startDate)} - {formatDatePretty(schedulerPreview.endDate)}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Unassigned Bookings</p>
                  <p className="text-lg font-semibold text-amber-600">{schedulerPreview.trips.length}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Available Assets ({schedulerPreview.assets.length})</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {schedulerPreview.assets.slice(0, 5).map(asset => (
                      <div key={asset.id} className="text-sm text-gray-600 flex items-center gap-2">
                        <Package className="w-3 h-3" />
                        {asset.name}
                      </div>
                    ))}
                    {schedulerPreview.assets.length > 5 && (
                      <p className="text-xs text-gray-500">+{schedulerPreview.assets.length - 5} more</p>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Available Team ({schedulerPreview.teamMembers.length})</p>
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {schedulerPreview.teamMembers.slice(0, 5).map(teamMember => (
                      <div key={teamMember.id} className="text-sm text-gray-600 flex items-center gap-2">
                        <Users className="w-3 h-3" />
                        {teamMember.name}
                      </div>
                    ))}
                    {schedulerPreview.teamMembers.length > 5 && (
                      <p className="text-xs text-gray-500">+{schedulerPreview.teamMembers.length - 5} more</p>
                    )}
                  </div>
                </div>
              </div>
              
              {schedulerPreview.trips.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Check className="w-12 h-12 mx-auto mb-2 text-green-500" />
                  <p>All bookings in this period are already assigned!</p>
                </div>
              )}
              
              {schedulerPreview.trips.length > 0 && (
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowSchedulerConfirm(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={executeAutoSchedule}
                    disabled={isScheduling}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isScheduling ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Scheduling...
                      </>
                    ) : (
                      <>
                        <Rocket className="w-4 h-4 mr-2" />
                        Auto-Schedule {schedulerPreview.trips.length} Booking{schedulerPreview.trips.length !== 1 ? 's' : ''}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Health Check Modal */}
      <Dialog open={healthModalOpen} onOpenChange={setHealthModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-green-600" />
              Schedule Health Check
            </DialogTitle>
            <DialogDescription>
              Conflicts and issues detected in your schedule
            </DialogDescription>
          </DialogHeader>
          
          {healthLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Analyzing schedule...</span>
            </div>
          ) : healthData ? (
            <div className="space-y-4">
              {healthData.error ? (
                <div className="text-center py-8 text-red-600">
                  <AlertCircle className="w-12 h-12 mx-auto mb-2" />
                  <p>{healthData.error}</p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-600">{healthData.summary?.totalBookings || 0}</p>
                      <p className="text-sm text-gray-600">Total Bookings</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-amber-600">{healthData.summary?.conflicts || 0}</p>
                      <p className="text-sm text-gray-600">Conflicts</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{healthData.summary?.unassigned || 0}</p>
                      <p className="text-sm text-gray-600">Unassigned</p>
                    </div>
                  </div>
                  
                  {/* Conflicts */}
                  {healthData.conflicts && healthData.conflicts.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-red-600 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Conflicts ({healthData.conflicts.length})
                      </h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {healthData.conflicts.map((conflict, idx) => (
                          <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded text-sm">
                            <p className="font-medium text-red-800">{conflict.type}</p>
                            <p className="text-red-700">{conflict.message}</p>
                            {conflict.bookings && (
                              <p className="text-red-600 text-xs mt-1">
                                Bookings: {conflict.bookings.join(', ')}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Warnings */}
                  {healthData.warnings && healthData.warnings.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-amber-600 mb-2 flex items-center gap-2">
                        <AlertCircle className="w-4 h-4" />
                        Warnings ({healthData.warnings.length})
                      </h3>
                      <div className="space-y-2 max-h-48 overflow-y-auto">
                        {healthData.warnings.map((warning, idx) => (
                          <div key={idx} className="p-3 bg-amber-50 border border-amber-200 rounded text-sm">
                            <p className="font-medium text-amber-800">{warning.type}</p>
                            <p className="text-amber-700">{warning.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* All Clear */}
                  {(!healthData.conflicts || healthData.conflicts.length === 0) && 
                   (!healthData.warnings || healthData.warnings.length === 0) && (
                    <div className="text-center py-8 text-green-600">
                      <Check className="w-12 h-12 mx-auto mb-2" />
                      <p className="font-semibold">All Clear!</p>
                      <p className="text-sm text-gray-600">No conflicts or issues detected</p>
                    </div>
                  )}
                </>
              )}
              
              <Button
                onClick={() => setHealthModalOpen(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      
      {/* Edit Booking Modal */}
      {editingBooking && (
        <EditBookingModal
          isOpen={true}
          trip={editingBooking}
          assets={assets}
          teamMembers={teamMembers}
          roles={roles}
          services={services}
          allBookings={trips}
          onSave={async (bookingId, bookingData) => {
            try {
              const cleanId = (v) => (!v || v === 'unassigned') ? null : v;

              const updatePayload = {
                bookingDate: bookingData.date,
                startTime: bookingData.startTime,
                durationMinutes: bookingData.duration,
                internalNotes: bookingData.notes || '',
                teamAssignments: (bookingData.teamAssignments || []).map(a => ({
                  ...a,
                  teamId: cleanId(a.teamId) || '',
                })),
                customerName: bookingData.customerName,
                customerEmail: bookingData.customerEmail,
                customerPhone: bookingData.customerPhone,
                pickupLocation: bookingData.pickupLocation,
                paymentStatus: bookingData.paymentStatus,
                checkedIn: bookingData.checkedIn,
                timezone: bookingData.timezone,
                assetId: cleanId(bookingData.assetId),
                partySize: bookingData.partySize,
                emergencyContactNumber: bookingData.emergencyContactNumber,
                selectedAddOns: bookingData.selectedAddOns,
                city: bookingData.city,
                country: bookingData.country,
                notifyTeam: bookingData.notifyTeam ?? false,
                notifyCustomer: bookingData.notifyCustomer ?? false,
              };
              
              await orgApi.updateBooking(bookingId, updatePayload);
              
              // Close modal first to give visual feedback
              setEditModalOpen(false);
              setEditingBooking(null);
              toast.success('Booking updated successfully!');
              
              // Small delay to ensure DB transaction is committed before refetching
              await new Promise(resolve => setTimeout(resolve, 300));
              
              // Force refresh the data
              await fetchData();
              if (onRefresh) onRefresh();
              
              // Also invalidate the ScheduleTab SWR cache (uses 'daily-schedule' key prefix)
              globalMutate(key => Array.isArray(key) && key[0] === 'daily-schedule');
            } catch (error) {
              console.error('❌ Error updating booking:', error);
              console.error('Error details:', error.response?.data);
              // Handle booking conflict error specifically
              const errorDetail = error.response?.data?.detail;
              if (typeof errorDetail === 'object' && errorDetail?.message) {
                toast.error(errorDetail.message);
              } else if (typeof errorDetail === 'string') {
                toast.error(errorDetail);
              } else {
                toast.error('Failed to update booking');
              }
            }
          }}
          onClose={() => {
            setEditModalOpen(false);
            setEditingBooking(null);
          }}
          onGeneratePaymentLink={handleGeneratePaymentLink}
        />
      )}

      {/* Waiver Details Modal */}
      <Dialog open={showWaiverDetailsModal} onOpenChange={setShowWaiverDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Waiver Details</DialogTitle>
            <DialogDescription>
              Signed waiver information for this booking
            </DialogDescription>
          </DialogHeader>
          
          {selectedWaiverBooking && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Booking ID</p>
                  <p className="font-semibold">{selectedWaiverBooking.confirmationCode || selectedWaiverBooking.id}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">PIN Code</p>
                  <p className="font-semibold text-blue-600">{selectedWaiverBooking.waiverOtp || 'Not set'}</p>
                </div>
              </div>
              
              {selectedWaiverBooking.waiverSignedAt && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Signed At</p>
                  <p className="text-sm text-gray-600">
                    {new Date(selectedWaiverBooking.waiverSignedAt).toLocaleString()}
                  </p>
                </div>
              )}
              
              {selectedWaiverBooking.waiverData && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Waiver Data</p>
                  <div className="max-h-64 overflow-y-auto p-3 bg-gray-50 rounded text-sm">
                    <pre className="whitespace-pre-wrap">
                      {JSON.stringify(selectedWaiverBooking.waiverData, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
              
              <Button
                onClick={() => setShowWaiverDetailsModal(false)}
                className="w-full"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Payment Link Modal for "Pay Later" bookings */}
      <SharePaymentLinkModal
        open={showPaymentLinkModal}
        onClose={() => setShowPaymentLinkModal(false)}
        checkoutUrl={paymentLinkData?.checkoutUrl}
        customerEmail={paymentLinkData?.customerEmail}
        customerPhone={paymentLinkData?.customerPhone}
        bookingDetails={paymentLinkData?.bookingDetails}
      />

      {/* View Ticket Modal - Compact size */}
      <Dialog open={viewTicketModalOpen} onOpenChange={(open) => {
        setViewTicketModalOpen(open);
        if (!open) setViewTicketUrl(null);
      }}>
        <DialogContent className="max-w-md w-[90vw] h-[70vh] p-0 overflow-hidden">
          <DialogHeader className="px-4 py-3 border-b bg-gray-50">
            <div className="flex items-center justify-between pr-8">
              <DialogTitle className="text-base font-semibold">
                Customer Ticket
              </DialogTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (viewTicketUrl) openLink(viewTicketUrl);
                }}
                className="text-xs h-7 px-2"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Open Full View
              </Button>
            </div>
          </DialogHeader>
          {viewTicketUrl ? (
            <iframe
              src={viewTicketUrl}
              className="w-full h-full border-0"
              title="Customer Ticket"
              style={{ minHeight: '400px' }}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Smart Schedule Panel */}
      <SmartSchedulePanel
        isOpen={smartScheduleOpen}
        onClose={() => setSmartScheduleOpen(false)}
        bookings={getVisibleBookings()}
        assets={assets}
        teamMembers={teamMembers}
        onScheduleApplied={async () => {
          await fetchData();
          if (onRefresh) onRefresh();
        }}
      />
    </TabPageLayout>
  );
};

export default CalendarScheduler;
